import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Button, SearchableSelect, CardContainer, StatusBadge, PaginationControls, usePagination } from '@tviklax/ui-components';

type ComplianceStatus = 'draft' | 'submitted' | 'docs-requested' | 'docs-resubmitted' | 'compliance-approved' | 'remittance-initiated' | 'remittance-completed' | 'locked';

interface ForexOrder {
  id: string;
  buyer: string;
  supplier: string;
  country: string;
  currency: string;
  totalValue: number;
  complianceStatus: ComplianceStatus;
  expectedShipmentDate: string;
}

interface AllForexComplianceViewProps {
  orders: ForexOrder[];
  onSelectOrder: (orderId: string) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
  margin: 0;
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 250px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;

  @media (max-width: 768px) {
    display: block;
    border: none;
  }
`;

const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.gray0};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
  border: none;

  &:last-child {
    text-align: right;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray0};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    border: 1px solid ${({ theme }) => theme.colors.gray200};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray900};

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => `${theme.spacing[2]} 0`};
    border: none;

    &:before {
      content: attr(data-label);
      font-weight: 600;
      color: ${({ theme }) => theme.colors.gray600};
      font-size: ${({ theme }) => theme.typography.fontSize.xs};
      text-transform: uppercase;
      letter-spacing: '0.05em';
    }
  }
`;

const ActionCell = styled(TableCell)`
  text-align: right;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    text-align: left;

    &:before {
      display: none;
    }
  }
`;

const StatusCell = styled(TableCell)`
  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing[8]} ${theme.spacing[6]}`};
  color: ${({ theme }) => theme.colors.gray500};

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.gray0};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray700};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const getStatusLabel = (status: ComplianceStatus): string => {
  const labels: Record<ComplianceStatus, string> = {
    'draft': '⚪ Draft',
    'submitted': '🔵 Submitted',
    'docs-requested': '🟡 Docs Requested',
    'docs-resubmitted': '🟠 Docs Resubmitted',
    'compliance-approved': '🟢 Compliance Approved',
    'remittance-initiated': '🔷 Remittance Initiated',
    'remittance-completed': '✅ Remittance Completed',
    'locked': '🔒 Locked',
  };
  return labels[status];
};

const getStatusVariant = (status: ComplianceStatus): 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'locked' => {
  const variants: Record<ComplianceStatus, 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'locked'> = {
    'draft': 'neutral',
    'submitted': 'info',
    'docs-requested': 'warning',
    'docs-resubmitted': 'warning',
    'compliance-approved': 'success',
    'remittance-initiated': 'success',
    'remittance-completed': 'success',
    'locked': 'locked',
  };
  return variants[status];
};

export const AllForexComplianceView: React.FC<AllForexComplianceViewProps> = ({ orders, onSelectOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const searchOptions = useMemo(() => {
    return ['All Orders', ...orders.map(o => `${o.id} — ${o.buyer}`)];
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!searchTerm || searchTerm === 'All Orders') {
      return orders;
    }
    const term = searchTerm.toLowerCase();
    return orders.filter(
      o => o.id.toLowerCase().includes(term) || o.buyer.toLowerCase().includes(term)
    );
  }, [orders, searchTerm]);

  const {
    currentPage,
    pageSize,
    paginatedItems,
    setCurrentPage,
    setPageSize,
  } = usePagination({ items: filteredOrders, initialPageSize: 10 });

  return (
    <Container>
      <PageHeader>
        <HeaderContent>
          <Title>Forex Compliance</Title>
          <Subtitle>Manage RBI-ready compliance packs for foreign currency payments</Subtitle>
        </HeaderContent>
      </PageHeader>

      <SearchContainer>
        <SearchableSelect
          options={searchOptions}
          value={searchTerm || 'All Orders'}
          onChange={(value) => {
            if (value && value !== 'All Orders') {
              const orderId = value.split(' — ')[0];
              onSelectOrder(orderId);
            } else {
              setSearchTerm(value);
            }
          }}
          placeholder="Search by order ID or buyer name..."
        />
      </SearchContainer>

      <StatsBar>
        <span>Total: {filteredOrders.length}</span>
        <span>Drafts: {filteredOrders.filter(o => o.complianceStatus === 'draft').length}</span>
        <span>In Review: {filteredOrders.filter(o => ['submitted', 'docs-requested', 'docs-resubmitted'].includes(o.complianceStatus)).length}</span>
        <span>Approved: {filteredOrders.filter(o => o.complianceStatus === 'compliance-approved').length}</span>
        <span>In Remittance: {filteredOrders.filter(o => ['remittance-initiated', 'remittance-completed'].includes(o.complianceStatus)).length}</span>
      </StatsBar>

      {filteredOrders.length === 0 ? (
        <CardContainer variant="elevated" padding="lg">
          <EmptyState>
            <p>No orders found matching your search.</p>
          </EmptyState>
        </CardContainer>
      ) : (
        <TableWrapper>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>Order ID</TableHeaderCell>
                <TableHeaderCell>Buyer</TableHeaderCell>
                <TableHeaderCell>Supplier</TableHeaderCell>
                <TableHeaderCell>Country</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableHeaderCell>Compliance Status</TableHeaderCell>
                <TableHeaderCell style={{ textAlign: 'right' }}>Action</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {paginatedItems.map(order => (
                <TableRow key={order.id}>
                  <TableCell data-label="Order ID">
                    <span style={{ fontWeight: 600, color: '#1f2937' }}>{order.id}</span>
                  </TableCell>
                  <TableCell data-label="Buyer">{order.buyer}</TableCell>
                  <TableCell data-label="Supplier">{order.supplier}</TableCell>
                  <TableCell data-label="Country">{order.country}</TableCell>
                  <TableCell data-label="Amount">
                    {order.currency} {order.totalValue.toLocaleString('en-IN')}
                  </TableCell>
                  <StatusCell data-label="Compliance Status">
                    <StatusBadge 
                      variant={getStatusVariant(order.complianceStatus)}
                      label={getStatusLabel(order.complianceStatus)}
                    />
                  </StatusCell>
                  <ActionCell>
                    <Button
                      onClick={() => onSelectOrder(order.id)}
                      variant="default"
                      size="sm"
                    >
                      View Details
                    </Button>
                  </ActionCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationControls
            totalItems={filteredOrders.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </TableWrapper>
      )}
    </Container>
  );
};
